import * as Service from 'types/service';
import { ButtonBase } from '@material-ui/core';
import { ChronoUnit, Duration, Instant } from 'js-joda';
import { ConnectDragSource } from 'react-dnd';
import { IconExclamationCircle, IconGrab, IconPin } from '@tocktix/pantry';
import { Typography } from 'bento';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { formatDurationAsHMText, formatDurationAsMText, millisToNextMinute } from 'utils/timeUtils';
import { partyCardStatusIndicatorCssDefs } from 'components/service/party/partyRedesignCard/partyCardCssDefs';
import { useIsCompactView } from 'hooks/useIsCompactView';
import { useMountEffect } from '@tock/hooks';
import { useState } from 'react';
import { useTimeSinceSeated } from 'hooks/partyHooks';

export interface PartyRedesignStatusIndicatorProps {
  /**
   * Callback drag ref. Must be attached to draggable portion of DOM.
   */
  dragRef?: ConnectDragSource;
  /**
   * Called when status indicator is clicked
   */
  onClick(): void;
  /**
   * The party being displayed
   */
  party: Service.Party;
  /**
   * How to treat the case where a party's table is not assigned
   */
  unassignedTreatment: 'dragIcon' | 'empty' | 'warning' | 'warningNoGrabCursor';
  /**
   * How to treat the case where a party's table is assigned
   */
  assignedTreatment: 'normal' | 'noGrabCursor';
  /** Whether to show hover and draggable cursor affordances. */
  isInteractive?: boolean;
}

/**
 * Specialized button for guest list cards that renders status
 * information based on the state of a party.
 */
const PartyRedesignStatusIndicator: React.FC<
  React.PropsWithChildren<PartyRedesignStatusIndicatorProps>
> = ({ dragRef, onClick, party, unassignedTreatment, assignedTreatment, isInteractive = true }) => {
  const isCompactView = useIsCompactView();
  const [currentInstant, setCurrentInstant] = useState<Instant>(Instant.now());
  const timeSinceSeated = useTimeSinceSeated(party);

  useMountEffect(() => {
    const millisToNextMin = millisToNextMinute(timeSinceSeated);
    setTimeout(() => {
      setCurrentInstant(Instant.now());
      setInterval(() => {
        setCurrentInstant(Instant.now());
      }, 60000);
    }, millisToNextMin);
  });

  switch (party.state) {
    case 'EXPECTED':
    case 'ARRIVED':
    case 'PARTIALLY_ARRIVED': {
      const { tables, visit, tableString, arrivalTime, type: partyType } = party;
      const hasPreAssignment = tables.length > 0;
      const isPinned = visit && visit.pinnedToTable;

      // We don't show the arrival timer for walkins, this amounts to showing double-timers on
      // the party card. Bookings only!
      let arrivalTimer = null;

      if (arrivalTime && partyType === 'Booking') {
        const timeArrived = Instant.ofEpochSecond(arrivalTime, 0);
        const timeSinceArrived = Duration.ofSeconds(
          timeArrived.until(currentInstant, ChronoUnit.SECONDS)
        );
        arrivalTimer = (
          <div css={partyCardStatusIndicatorCssDefs.arrivalTimer}>
            +{formatDurationAsMText(timeSinceArrived)}
          </div>
        );
      }

      /**
       * If there is no pre-assignment and the unassigned treatment is 'empty' we don't render a status.
       * (A past use case: slot customers in timeline view, though we no longer treat them this way.
       */
      if (!hasPreAssignment && unassignedTreatment === 'empty') {
        return null;
      }

      const showUnassignedWarning = unassignedTreatment.startsWith('warning');

      return hasPreAssignment ? (
        <ButtonBase
          ref={dragRef}
          css={partyCardStatusIndicatorCssDefs.root({
            isCompactView,
            noGrabCursor: assignedTreatment === 'noGrabCursor',
            isInteractive,
          })}
          disableRipple
          onClick={onClick}
          aria-label={`View details for party ${
            isPinned ? `pinned` : `assigned`
          } to ${tableString}`}
          data-testid="drag-button"
        >
          <Typography variant="bodyStrong" color="inherit" aria-hidden="true">
            {tableString}
          </Typography>
          {isPinned && <IconPin data-testid="pinIcon" size={100} />}
          {arrivalTimer}
        </ButtonBase>
      ) : (
        <ButtonBase
          ref={dragRef}
          css={partyCardStatusIndicatorCssDefs.root({
            showUnassignedWarning,
            isCompactView,
            noGrabCursor: unassignedTreatment === 'warningNoGrabCursor',
            isInteractive,
          })}
          disableRipple
          onClick={onClick}
          aria-label="View details for unassigned party"
          data-testid="drag-button"
        >
          {showUnassignedWarning ? (
            <IconExclamationCircle data-testid="alertIcon" color="fgDangerBase" />
          ) : unassignedTreatment === 'dragIcon' ? (
            <IconGrab data-testid="dragIcon" />
          ) : /* shouldn't happen, for completeness */
          null}
          {arrivalTimer}
        </ButtonBase>
      );
    }

    case 'PARTIALLY_SEATED':
    case 'SEATED': {
      return (
        <ButtonBase
          ref={dragRef}
          css={partyCardStatusIndicatorCssDefs.root({
            isCompactView,
            noGrabCursor: assignedTreatment === 'noGrabCursor',
            isInteractive,
          })}
          disableRipple
          onClick={onClick}
        >
          <Typography variant="bodyStrong" color="inherit">
            {party.tableString}
          </Typography>
          {timeSinceSeated ? (
            <Typography variant="captionStrong" color="subtle">
              {formatDurationAsHMText(timeSinceSeated)}
            </Typography>
          ) : null}
        </ButtonBase>
      );
    }

    case 'NO_SHOW':
    case 'CANCELED':
    case 'LEFT':
      return null;

    default:
      assertNever(party.state);
  }
};

export default PartyRedesignStatusIndicator;
